/**
 * Certificate and result documents: putting them in S3, and getting them back out.
 *
 * ── Nothing new here on purpose ───────────────────────────────────────────────────────────
 *
 * Amplify Storage is already configured and already used by receipts, nominations and PMS
 * attachments. This is the same three rules those settled, applied to certifications:
 *
 *   - a uuid in the key, so two files called `certificate.pdf` do not overwrite each other;
 *   - the `public/` prefix stripped before handing a key to Amplify v6, which re-adds it;
 *   - `validateObjectExistence` on download, so a key pointing at nothing fails with a sentence
 *     rather than opening a tab full of S3 error XML.
 *
 * The third one matters more here than elsewhere. A certification document is the evidence an
 * approver decides on; a link that silently resolves to an error page is worse than one that
 * says the file is missing, because the approver cannot tell which of the two they are looking
 * at.
 *
 * ── One thing that IS new: a thumbnail ───────────────────────────────────────────────────
 *
 * Certificates are phone photos and screenshots, so a megabyte or two each is ordinary. The card
 * grid shows them in a 132px band, and pointing those bands at the originals cost 4.7 MB and
 * nineteen seconds to draw three cards. Nothing in this stack resizes an image server-side, so a
 * downscaled JPEG is built in the browser at upload and stored beside the original under a
 * derived key — see makeCertificateThumbnail and certificateThumbnailKey. Records uploaded before
 * this have no thumbnail, which is why the reader falls back to the original rather than assuming
 * one is there.
 *
 * That existence check is a HeadObject issued by the browser, so it needs `HEAD` in the bucket's
 * CORS `AllowedMethods` — a bucket configured for `GET, PUT, POST` alone answers the preflight
 * with 403 and every download fails as a network error even though the object is right there.
 * `getCertificateUrl` therefore treats an unreachable probe as "could not verify" and falls back
 * to an unvalidated URL: `GET` is allowed, so the link still works. Only a probe that actually
 * reached S3 and came back 404 is reported as a missing file.
 *
 * ── A PDF gets a thumbnail too ───────────────────────────────────────────────────────────
 *
 * It did not, at first: a PDF was treated as un-previewable and the card printed its filename.
 * That is half a register showing its evidence and half showing `certificate.pdf`, and the
 * filename half is the useless half — a reader cannot tell one qualification from another by it.
 *
 * So the same `.thumb.jpg` beside the original now holds page one of a PDF, rendered by pdf.js in
 * the browser (see services/pdfPageImage). Nothing about the key, the signing or the fallback
 * changes; only the set of documents that can produce a thumbnail widens. What DOES differ is the
 * last resort: an image with no stored thumbnail falls back to the original, because an `<img>` can
 * draw it, and a PDF with no stored thumbnail falls back to `getCertificatePdfPreviewUrl`, which
 * downloads it and renders page one on the spot. Both exist for the records uploaded before
 * thumbnails did.
 */

import { uploadData, getUrl } from 'aws-amplify/storage';
import { v4 as uuidv4 } from 'uuid';
import {
  isPdfDocument,
  renderPdfFirstPage,
  renderPdfFirstPageFromUrl,
} from './pdfPageImage';

/** Extensions a certificate or result document may have. */
export const ALLOWED_CERTIFICATE_TYPES = ['pdf', 'jpg', 'jpeg', 'png'];
export const MAX_CERTIFICATE_SIZE_MB = 10;

/** How long a generated download link stays valid, in seconds. */
export const CERTIFICATE_URL_TTL_SECONDS = 900;

export type CertificateUrlFailure = 'MISSING_KEY' | 'NOT_FOUND' | 'ERROR';

export class CertificateUrlError extends Error {
  constructor(public readonly kind: CertificateUrlFailure, message: string) {
    super(message);
    this.name = 'CertificateUrlError';
  }
}

/**
 * The S3 key a certificate is stored under.
 *
 * Scoped by the OWNER's email rather than by certification id, because the id does not exist yet
 * at upload time — the file is chosen before the record is created, and a key that had to wait
 * for an id would mean uploading only on save and losing the file if the save failed.
 *
 * The email is slugged rather than embedded raw: an S3 key tolerates `@` and `.`, but the
 * resulting path is awkward to read in the console and awkward to match in a bucket policy.
 */
export function buildCertificateKey(
  employeeEmail: string,
  fileName: string,
  unique: string = uuidv4()
): string {
  const owner = String(employeeEmail || 'unknown').trim().toLowerCase().replace(/[^\w.-]+/g, '_');
  const safeName = String(fileName || 'certificate').replace(/[^\w.-]+/g, '_');
  return `certifications/${owner}/${unique}-${safeName}`;
}

/** Amplify v6 re-adds `public/`; a stored key carrying one would resolve to `public/public/…`. */
export function normalizeStorageKey(s3Key: string): string {
  const trimmed = String(s3Key || '').trim();
  return trimmed.startsWith('public/') ? trimmed.slice('public/'.length) : trimmed;
}

/** Client-side validation. The same limits the form shows, so the two cannot disagree. */
export function validateCertificateFile(file: File): string | null {
  const parts = file.name.split('.');
  const ext = parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
  if (!ALLOWED_CERTIFICATE_TYPES.includes(ext)) {
    return `File type ".${ext}" is not allowed. Allowed: ${ALLOWED_CERTIFICATE_TYPES.join(', ').toUpperCase()}`;
  }
  if (file.size > MAX_CERTIFICATE_SIZE_MB * 1024 * 1024) {
    return `File exceeds the maximum size of ${MAX_CERTIFICATE_SIZE_MB}MB.`;
  }
  return null;
}

/**
 * The longest edge, in pixels, of the thumbnail stored beside a certificate image.
 *
 * 640 is enough for the card's 280×132 media band on a 2× display and still lands a JPEG in the
 * tens of kilobytes. The band is the only place a thumbnail is ever shown — clicking it opens the
 * original — so anything larger is bytes nobody sees.
 */
export const CERTIFICATE_THUMBNAIL_MAX_EDGE = 640;
const CERTIFICATE_THUMBNAIL_QUALITY = 0.72;

/**
 * Where the thumbnail for a stored certificate lives.
 *
 * Derived from the document key rather than recorded on the certification, so no schema field, no
 * migration, and no second value that can disagree with the first. The suffix cannot collide: the
 * key already carries a uuid.
 */
export function certificateThumbnailKey(documentKey: string): string {
  return `${normalizeStorageKey(documentKey)}.thumb.jpg`;
}

/**
 * Downscale an image file to a thumbnail, or null when it cannot be done.
 *
 * ── Why this exists at all ────────────────────────────────────────────────────────────────
 *
 * A certificate is usually a phone photo or a screenshot: 1–2 MB is ordinary and 10 MB is
 * allowed. The card grid drew those originals into a 132px band, so opening All Certifications
 * downloaded several megabytes to render a few thumbnails — measured at 4.7 MB and 19 seconds for
 * three cards. Nothing server-side resizes images (S3 and Amplify Storage only store what they
 * are given), so the resize has to happen where the file already is: in the browser, once, at
 * upload.
 *
 * ── A PDF takes the other branch ─────────────────────────────────────────────────────────
 *
 * It used to take none: a PDF returned null here and the card printed its filename. It now goes
 * through pdf.js instead of the image decoder, and comes out as the same kind of JPEG under the
 * same key — so everything downstream of this function, the upload and the card alike, cannot tell
 * which of the two branches produced the thumbnail it is handed. That is the point of doing it
 * here rather than in the card.
 *
 * Null on every failure — a browser without canvas encoding, a file the decoder rejects, a PDF
 * that pdf.js cannot parse. The caller treats a missing thumbnail as normal and falls back, which
 * is what every record uploaded before this existed does.
 */
export async function makeCertificateThumbnail(file: File): Promise<Blob | null> {
  if (typeof document === 'undefined' || typeof URL?.createObjectURL !== 'function') return null;

  /* Both the name and the type are consulted, because neither is reliable alone: a file picked
     from some Windows shares arrives with an empty `type`, and a file renamed to `.pdf` reports
     the real one. Either saying PDF is enough — pdf.js rejecting the bytes is handled. */
  if (isPdfDocument(file.name) || /pdf$/i.test(file.type || '')) {
    return renderPdfFirstPage(await file.arrayBuffer(), CERTIFICATE_THUMBNAIL_MAX_EDGE);
  }
  if (!/^image\//i.test(file.type || '')) return null;

  const objectUrl = URL.createObjectURL(file);
  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const element = new Image();
      element.onload = () => resolve(element);
      element.onerror = () => reject(new Error('The image could not be decoded.'));
      element.src = objectUrl;
    });

    const longest = Math.max(image.naturalWidth, image.naturalHeight);
    if (!longest) return null;
    /* Never UPSCALE. A small image is already its own thumbnail, and re-encoding it as a JPEG
       would cost quality and could even cost bytes. */
    const scale = Math.min(1, CERTIFICATE_THUMBNAIL_MAX_EDGE / longest);
    if (scale === 1 && /jpe?g$/i.test(file.type)) return null;

    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
    canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
    const context = canvas.getContext('2d');
    if (!context) return null;
    /* A white ground, because the target is JPEG: a transparent PNG drawn straight onto the
       canvas would flatten its transparency to black. */
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(image, 0, 0, canvas.width, canvas.height);

    return await new Promise<Blob | null>((resolve) => {
      if (typeof canvas.toBlob !== 'function') {
        resolve(null);
        return;
      }
      canvas.toBlob((blob) => resolve(blob), 'image/jpeg', CERTIFICATE_THUMBNAIL_QUALITY);
    });
  } catch (error) {
    console.warn('[CertificationDocuments] Could not build a thumbnail for', file.name, error);
    return null;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

/**
 * Upload a certificate and return the key to store on the record.
 *
 * Returns the key rather than a URL. A signed URL expires, so persisting one would work until it
 * quietly stopped; the key is the durable reference and the URL is derived from it per click.
 *
 * A thumbnail is uploaded beside it, best-effort. It is deliberately NOT part of the promise the
 * caller depends on: the record references the original, the card falls back to the original when
 * no thumbnail is there, and failing a certificate upload because a nicety could not be encoded
 * would be the wrong trade every time.
 */
export async function uploadCertificate(
  file: File,
  employeeEmail: string
): Promise<{ key: string; fileName: string }> {
  const problem = validateCertificateFile(file);
  if (problem) throw new Error(problem);

  const key = buildCertificateKey(employeeEmail, file.name);
  await uploadData({ key, data: file, options: { contentType: file.type } }).result;

  try {
    const thumbnail = await makeCertificateThumbnail(file);
    if (thumbnail) {
      await uploadData({
        key: certificateThumbnailKey(key),
        data: thumbnail,
        options: { contentType: 'image/jpeg' },
      }).result;
    }
  } catch (error) {
    console.warn('[CertificationDocuments] The thumbnail for', key, 'could not be stored.', error);
  }

  return { key, fileName: file.name };
}

/** Extensions a browser can put in an `<img>`. A PDF certificate is a document, not an image. */
const PREVIEWABLE_IMAGE_TYPES = ['jpg', 'jpeg', 'png'];

/**
 * Can this stored document be rendered as a thumbnail?
 *
 * Certificates are uploaded as PDF or as an image, and the two need different treatment on a card:
 * an image is shown, a PDF gets a placeholder. Nothing here can render a PDF page, and pointing an
 * `<img>` at one produces the browser's broken-image icon — the specific thing a card grid must
 * never show.
 */
export function isPreviewableCertificateImage(documentKey?: string | null): boolean {
  const key = normalizeStorageKey(documentKey || '');
  if (!key) return false;
  const parts = key.split('.');
  return parts.length > 1 && PREVIEWABLE_IMAGE_TYPES.includes(parts[parts.length - 1].toLowerCase());
}

/**
 * Could a stored `.thumb.jpg` exist beside this document?
 *
 * Wider than `isPreviewableCertificateImage`, and the two are not the same question. That one asks
 * whether the ORIGINAL can go in an `<img>` — a PDF cannot, and that is still true. This one asks
 * whether a thumbnail may have been built FROM it, which a PDF can, because the thumbnail is a
 * JPEG whatever the document was. Signing the one for the other is what lets a PDF card draw in a
 * single request, the same as a photo.
 */
export function canHaveStoredThumbnail(documentKey?: string | null): boolean {
  const key = normalizeStorageKey(documentKey || '');
  return isPreviewableCertificateImage(key) || isPdfDocument(key);
}

/**
 * A URL for displaying a certificate image inline, or null if there is nothing to display.
 *
 * ── Why this is not `getCertificateUrl` ───────────────────────────────────────────────────
 *
 * Deliberately WITHOUT `validateObjectExistence`, which is the opposite of the choice the download
 * path makes, for a reason that only applies to a thumbnail: an `<img>` that fails already has a
 * fallback in `onError`, so the caller shows its placeholder either way and the probe changes
 * nothing on screen. What the probe does cost is a HeadObject per card — a network round trip each,
 * on a grid that renders dozens, and every one of them fails while the bucket CORS omits HEAD.
 * Signing without it is local: no request leaves the browser until the `<img>` itself loads.
 *
 * Returns null rather than throwing. A missing thumbnail is a cosmetic absence on a card that
 * still shows the certification; the download path is where a missing file is worth a sentence.
 */
export async function getCertificatePreviewUrl(documentKey?: string | null): Promise<string | null> {
  if (!isPreviewableCertificateImage(documentKey)) return null;
  const key = normalizeStorageKey(documentKey || '');
  try {
    const result = await getUrl({ key, options: { expiresIn: CERTIFICATE_URL_TTL_SECONDS } });
    return result.url.toString();
  } catch (error) {
    console.warn('[CertificationDocuments] Could not build a preview URL for', key, error);
    return null;
  }
}

/**
 * A URL for the small thumbnail stored beside a certificate image, or null.
 *
 * Signed BLIND, like the preview above, and for a sharper version of the same reason: whether the
 * thumbnail exists cannot be known without a HeadObject, and the bucket's CORS may not allow one.
 * So the caller renders this URL and treats a load failure as "no thumbnail" — which is exactly
 * the state of every certificate uploaded before thumbnails existed, and the reason the caller
 * must hold the original URL as its fallback.
 */
export async function getCertificateThumbnailUrl(documentKey?: string | null): Promise<string | null> {
  if (!canHaveStoredThumbnail(documentKey)) return null;
  const key = certificateThumbnailKey(documentKey || '');
  try {
    const result = await getUrl({ key, options: { expiresIn: CERTIFICATE_URL_TTL_SECONDS } });
    return result.url.toString();
  } catch (error) {
    console.warn('[CertificationDocuments] Could not build a thumbnail URL for', key, error);
    return null;
  }
}

/**
 * Page one of a PDF certificate as a local image URL, rendered here and now, or null.
 *
 * ── The last resort, and only for PDFs uploaded before thumbnails covered them ────────────
 *
 * A PDF uploaded today gets its `.thumb.jpg` at upload and never reaches this function. The ones
 * already in the bucket have no thumbnail and no `<img>`-able original either, so the choice for
 * them is between rendering the PDF in the browser once, or printing a filename forever. This is
 * the first of those: download the document, hand it to pdf.js, put the JPEG behind an object URL
 * the card can use as an `<img>` source.
 *
 * Deliberately NOT cheap, and deliberately not used unless the thumbnail has already failed. It
 * downloads the whole PDF — hundreds of kilobytes for a certificate — and parses it.
 *
 * ── Results are cached for the life of the tab, including the failures ────────────────────
 *
 * By promise rather than by value, so two cards sharing a document (a retake keeps the original
 * attempt's file) render it once instead of racing and leaking the loser's object URL. A null is
 * cached too: a PDF that cannot be rendered cannot be rendered, and re-downloading it every time
 * a filter changes would turn one cosmetic gap into a stream of traffic.
 *
 * Object URLs are never revoked, which is the honest trade rather than an oversight: the card that
 * would own the revoke can unmount and remount as the grid filters, and a revoked URL still held
 * by this cache is a broken image. What it costs is bounded — one downscaled JPEG per distinct PDF
 * a reader actually looked at, released when the tab closes.
 */
const renderedPdfPages = new Map<string, Promise<string | null>>();

export function getCertificatePdfPreviewUrl(documentKey?: string | null): Promise<string | null> {
  const key = normalizeStorageKey(documentKey || '');
  if (!key || !isPdfDocument(key)) return Promise.resolve(null);

  const cached = renderedPdfPages.get(key);
  if (cached) return cached;

  const pending = (async () => {
    try {
      // Signed blind, like the thumbnail above: a failure here is the same cosmetic gap, and the
      // HeadObject that would prove the object exists is the request the bucket CORS may refuse.
      const signed = await getUrl({ key, options: { expiresIn: CERTIFICATE_URL_TTL_SECONDS } });
      const page = await renderPdfFirstPageFromUrl(
        signed.url.toString(),
        CERTIFICATE_THUMBNAIL_MAX_EDGE
      );
      if (!page || typeof URL?.createObjectURL !== 'function') return null;
      return URL.createObjectURL(page);
    } catch (error) {
      console.warn('[CertificationDocuments] Could not render page one of', key, error);
      return null;
    }
  })();

  renderedPdfPages.set(key, pending);
  return pending;
}

/**
 * Did the existence probe fail without ever getting an answer from S3?
 *
 * A blocked CORS preflight, an offline browser and a blackholed request all surface the same way:
 * a network-shaped error carrying no HTTP status. That says nothing about whether the object
 * exists, so it must not be reported as a missing file — the distinction the whole check exists
 * to draw would be inverted, telling an approver the evidence is gone when it is merely unverified.
 */
function isExistenceCheckUnreachable(error: any): boolean {
  if (error?.$metadata?.httpStatusCode) return false;
  const text = `${error?.name || ''} ${error?.message || ''}`;
  return /network|failed to fetch|load failed|cors|timeout|aborted/i.test(text);
}

/**
 * A time-limited download URL for a stored certificate.
 *
 * `validateObjectExistence` is the point rather than an optimisation — see the header, including
 * why an unreachable probe falls through to an unvalidated URL rather than failing the download.
 */
export async function getCertificateUrl(
  documentKey?: string | null,
  displayName?: string
): Promise<string> {
  const key = normalizeStorageKey(documentKey || '');
  if (!key) {
    throw new CertificateUrlError(
      'MISSING_KEY',
      `"${displayName || 'This document'}" has no stored file reference and cannot be opened.`
    );
  }
  try {
    const result = await getUrl({
      key,
      options: { expiresIn: CERTIFICATE_URL_TTL_SECONDS, validateObjectExistence: true },
    });
    return result.url.toString();
  } catch (error: any) {
    if (isExistenceCheckUnreachable(error)) {
      console.warn(
        '[CertificationDocuments] Could not verify that',
        key,
        'exists; serving an unvalidated URL. Add HEAD to the bucket CORS AllowedMethods to restore the check.',
        error
      );
      try {
        const result = await getUrl({ key, options: { expiresIn: CERTIFICATE_URL_TTL_SECONDS } });
        return result.url.toString();
      } catch (fallbackError) {
        // Signing failed too, so this is not a CORS-blocked probe over a healthy bucket. Fall
        // through and report it the same way as any other failure rather than leaking a raw error.
        console.error('[CertificationDocuments] Unvalidated URL also failed for', key, fallbackError);
      }
    }
    const notFound =
      error?.name === 'NotFound' ||
      error?.$metadata?.httpStatusCode === 404 ||
      /not\s*found/i.test(String(error?.message || ''));
    throw new CertificateUrlError(
      notFound ? 'NOT_FOUND' : 'ERROR',
      notFound
        ? `"${displayName || 'This document'}" is no longer in storage.`
        : `"${displayName || 'This document'}" could not be opened. Try again.`
    );
  }
}
